/**
 * TileRenderer - Renders a single tile with header/footer slots
 *
 * This component is part of the new slot-based architecture and renders
 * tiles directly from GridCell data with support for tile-header and tile-footer.
 *
 * This is separate from the legacy PanelTile to allow gradual migration.
 */

import React, {useMemo} from 'react';

import {getMediaMetadata} from '../../../../util/media';
import {getCustomRunName} from '../../../../util/runs';
import {createTileMediaSpec, type TileMediaSpec} from '../../../MediaCard';
import MessageMediaNotFound from '../../../MessageMediaNotFound';
import {getGroupedMediaDimensions} from '../../dimensions';
import {MediaBrowserPanelTileTrackingContextProvider} from '../../MediaBrowserPanelTileTrackingContext';
import {getModeConfig} from '../../modes/getModeConfig';
import {getFitSettingsForMediaType} from '../../sizing/getFitSettingsForMediaType';
import {TileMediaLoader} from '../../TileMediaLoader';
import {CompareFitTile} from '../../zoom/CompareFitTile';
import {TileFooterSlot} from '../slots/TileFooterSlot';
import {TileHeaderSlot} from '../slots/TileHeaderSlot';
import {type TileRendererProps} from './types';

/**
 * Renders a tile with optional header and footer slots.
 *
 * @param props - TileRendererProps
 * @returns React element
 */
export const TileRenderer: React.FC<TileRendererProps> = ({
  cell,
  tileIndex,
  cardWidth,
  cardHeight,
  config,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  stepMeta,
  updateConfig,
  tileHeaderControls,
  tileFooterControls,
  columnIndex,
  rowIndex,
  isCompareSynced: isSynced = false,
}) => {
  // Extract values conditionally to avoid accessing undefined properties
  const {
    run,
    tileSpec,
    step: cellStep,
    mediaIndex: cellMediaIndex,
    mediaKey,
    maskOptions,
  } = cell.isEmpty ? ({} as any) : cell;
  const runSignature = cell.isEmpty ? undefined : tileSpec.runSignature;

  const tileMediaSpec: TileMediaSpec | null = useMemo(
    () =>
      cell.isEmpty
        ? null
        : createTileMediaSpec({
            mediaType,
            run,
            mediaIndex: tileSpec?.mediaIndex ?? cellMediaIndex,
            mediaKey,
            mediaStep: tileSpec?.mediaStep ?? cellStep,
            mediaSliderKey,
            runSignature: runSignature!,
          }),
    [
      cell.isEmpty,
      mediaType,
      run,
      cellMediaIndex,
      mediaKey,
      cellStep,
      mediaSliderKey,
      runSignature,
      tileSpec,
    ]
  );

  const runNotes = cell.isEmpty ? undefined : run.notes;
  const customRunName = cell.isEmpty
    ? undefined
    : getCustomRunName(run.name, false, customRunNames);

  const tileAndPanelControls = useMemo(
    () => ({
      segmentationMaskControl: config.segmentationMaskConfig,
      boundingBoxControl: config.boundingBoxConfig,
    }),
    [config.segmentationMaskConfig, config.boundingBoxConfig]
  );

  const metadata = cell.isEmpty
    ? null
    : getMediaMetadata(run.history, mediaKey, cell.step, mediaSliderKey);
  const groupedDimensions = cell.isEmpty
    ? null
    : getGroupedMediaDimensions(metadata);

  // Create a stable key for the maskKeys to prevent unnecessary remounts
  const stableMaskKey =
    !cell.isEmpty && maskOptions
      ? [...maskOptions.maskKeys].sort().join(',')
      : '';
  const componentKey = `card-${tileIndex}-${mediaKey}-${stableMaskKey}`;

  // Memoize the props object to prevent unnecessary re-renders
  const tileMediaLoaderProps = useMemo(
    () =>
      cell.isEmpty || !tileMediaSpec
        ? null
        : {
            tileMediaSpec,
            attemptNonexistentSteps: !config.snapToExistingStep,
            run,
            runSignature,
            runNotes,
            customRunName,
            labels: [], // Labels are now in the tile header via slots
            globalStep: tileMediaSpec.mediaStep ?? cellStep,
            mediaKey,
            mediaIndex: tileMediaSpec.mediaIndex ?? cellMediaIndex,
            width: cardWidth,
            height: cardHeight,
            mediaWidth: groupedDimensions?.width,
            mediaHeight: groupedDimensions?.height,
            actualSize: getFitSettingsForMediaType(config, mediaType)
              .actualSize,
            controls: tileAndPanelControls,
            mediaPanelRef: panelRef,
            moleculeConfig: config.moleculeConfig,
            disableRunLink: disableRunLinks,
            maskOptions,
            config,
            omitCaptions: true,
          },
    [
      cell.isEmpty,
      tileMediaSpec,
      run,
      runSignature,
      runNotes,
      customRunName,
      cellStep,
      mediaKey,
      cellMediaIndex,
      cardWidth,
      cardHeight,
      groupedDimensions?.width,
      groupedDimensions?.height,
      tileAndPanelControls,
      panelRef,
      disableRunLinks,
      maskOptions,
      config,
      mediaType,
    ]
  );

  // Center content vertically in compare mode without fan-out.
  // With fan-out, or in gallery/grid mode, content is aligned to the top
  // of the cell in order to align media with and without captions.
  // This also requires allowing the cell to take the full height of the column,
  // so captions render at the bottom of the column, not under the media.
  const shouldCenterContentVertically = useMemo(() => {
    return config.mode === 'compare' && !config.compareSettings?.fanOutVariable;
  }, [config.mode, config.compareSettings?.fanOutVariable]);
  // Content-sized layout: the tile sizes to its own content.
  const contentSizedTiles = getModeConfig(config.mode).shouldSizeTilesByContent(
    config,
    mediaType
  );
  // Video.js sizes from its parent container, so video tiles otherwise need a
  // full-height wrapper to measure the panel content area. (In a content-sized
  // layout the video pins its own viewport, so it doesn't fill.)
  const shouldFillTileHeight =
    !contentSizedTiles &&
    (shouldCenterContentVertically || mediaType === 'video');

  if (cell.isEmpty) {
    // No background here — the cell-level wrapper in GridRenderer owns the background.
    // A transparent placeholder with the tile dimensions keeps the grid stable.
    return (
      <div
        style={{
          width: cardWidth,
          height: cardHeight,
        }}
      />
    );
  }

  return (
    <MediaBrowserPanelTileTrackingContextProvider
      key={componentKey}
      mediaKey={mediaKey}
      panelId={panelRef.id}
      runName={run.name}
      disableVideoStream={config.disableVideoStream ?? false}>
      <div
        className={`tile-${mediaType} flex flex-col w-full max-h-full ${shouldFillTileHeight ? 'h-full' : 'h-auto'} items-center min-h-0`}>
        {tileHeaderControls && (
          <div className="tile-header align-baseline text-ellipsis w-full flex-shrink-0">
            <TileHeaderSlot
              controls={tileHeaderControls}
              cell={cell}
              disableRunLinks={disableRunLinks}
              config={config}
              updateConfig={updateConfig}
              stepMeta={stepMeta}
              columnIndex={columnIndex}
              rowIndex={rowIndex}
            />
          </div>
        )}
        <div
          className={`flex justify-center w-full min-w-0 min-h-0 ${contentSizedTiles ? 'flex-none' : 'flex-1 overflow-hidden'} ${shouldCenterContentVertically ? 'items-center' : 'items-start'}`}>
          {cell.isFanOutStepNotFound ? (
            <div
              className="ui card image-card content-card mt-6 max-w-full"
              data-test="image-content-card"
              style={{
                width: cardWidth,
                maxHeight: cardHeight,
              }}>
              <div
                className="image-card-image w-full"
                style={{maxHeight: cardHeight}}>
                <div className="flex items-center justify-center">
                  <MessageMediaNotFound
                    runName={run.name}
                    stepIndex={cellStep}
                    mediaKey={mediaKey}
                    mediaIndex={cellMediaIndex}
                    maxWidth={cardWidth}
                    height={cardHeight}
                  />
                </div>
              </div>
            </div>
          ) : (
            tileMediaLoaderProps &&
            (isSynced ? (
              <CompareFitTile {...tileMediaLoaderProps} />
            ) : (
              <TileMediaLoader {...tileMediaLoaderProps} />
            ))
          )}
        </div>
        {tileFooterControls && (
          // w-0 + min-w-full: fill the tile's width but never widen it
          <div className="tile-footer w-0 min-w-full flex-shrink-0 bg-wb-surface">
            <TileFooterSlot
              controls={tileFooterControls}
              cell={cell}
              config={config}
            />
          </div>
        )}
      </div>
    </MediaBrowserPanelTileTrackingContextProvider>
  );
};
